<template>
  <div>
    <!-- Modal Trigger Button -->
    <b-button @click="showModal = true" variant="primary" class="add-resource-btn">
      <feather-icon icon="PlusIcon" size="16" />
    </b-button>

    <!-- Create Sub Category Modal -->
    <b-modal
      v-model="showModal"
      title="Create New Sub Category"
      size="xl"
      :hide-footer="true"
      :hide-header="true"
      :hide-header-close="true"
      :no-close-on-backdrop="true"
      :no-close-on-esc="true"
      centered
      @hidden="onModalHidden"
      id="create-subcategory-modal"
    >
      <!-- Modal Body -->
      <div class="modal-body p-0">
        <SubCategoryForm
          :form-data="subCategoryFormData"
          :loading="loading"
          :modal-mode="true"
          @submit="handleSubmit"
          @update:form-data="updateFormData"
          @cancel="closeModal"
          :is-modal="true"
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
import { BModal, BButton, BSpinner } from 'bootstrap-vue';
import { ref, reactive, computed } from '@vue/composition-api';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue';
import SubCategoryForm from './SubCategoryForm.vue';
import store from '@/store';
import SubCategoryStoreModule from './SubCategoryStoreModule.js';

export default {
  name: 'CreateSubCategoryModal',
  components: {
    BModal,
    BButton,
    BSpinner,
    FeatherIcon,
    SubCategoryForm,
    ToastificationContent,
  },
  emits: ['submit'],
  setup(props, { emit }) {
    const toast = useToast();
    const businessID = computed(() => store.state.user.businessID);

    // Register SubCategory store module if not already registered
    const SUBCATEGORY_STORE_MODULE_NAME = 'sub-category';
    if (!store.hasModule(SUBCATEGORY_STORE_MODULE_NAME)) {
      store.registerModule(SUBCATEGORY_STORE_MODULE_NAME, SubCategoryStoreModule);
    }

    // Reactive state
    const showModal = ref(false);
    const loading = ref(false);
    const subCategoryFormData = reactive({
      id: 0,
      subCategory: '',
      categoryType: '',
      furtherTax: 0,
      isActive: true,
      businessID: businessID.value,
    });

    // Methods
    const updateFormData = (newData) => {
      Object.assign(subCategoryFormData, newData);
    };

    const handleSubmit = async () => {
      console.log(
        '=== CreateSubCategoryModal handleSubmit',
        JSON.stringify(subCategoryFormData.value, null, 2)
      );

      loading.value = true;

      try {
        // Call the store action to create SubCategory
        const subCategoryResponse = await store.dispatch(
          'sub-category/saveSubCategory',
          subCategoryFormData
        );

        // Show success message
        toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: 'Sub Category created successfully!',
            variant: 'success',
            icon: 'CheckIcon',
          },
        });

        // Reset form data
        resetFormData();

        // Close modal
        closeModal();

        // Emit submit event to parent
        emit('submit', subCategoryResponse);
      } catch (error) {
        console.error('Error creating SubCategory:', error);

        // Show error message
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text:
              error?.response?.data?.message || 'Failed to create Sub Category. Please try again.',
            variant: 'danger',
            icon: 'AlertTriangleIcon',
          },
        });
      } finally {
        loading.value = false;
      }
    };

    const resetFormData = () => {
      Object.assign(subCategoryFormData, {
        id: 0,
        subCategory: '',
        categoryType: '',
        furtherTax: 0,
        isActive: true,
        businessID: businessID.value,
      });
    };

    const closeModal = () => {
      showModal.value = false;
    };

    const onModalHidden = () => {
      // Reset form when modal is hidden
      resetFormData();
    };

    return {
      // Reactive state
      showModal,
      loading,
      subCategoryFormData,

      // Methods
      updateFormData,
      handleSubmit,
      resetFormData,
      closeModal,
      onModalHidden,
    };
  },
};
</script>

<style lang="scss" scoped>
.modal-body {
  max-height: 60vh;
  overflow-y: auto;
  padding: 0;
}

::v-deep .modal-xl {
  max-width: 95%;
}

::v-deep .modal-header {
  border-bottom: 1px solid #dee2e6;
  padding: 1rem 1.5rem;
}

::v-deep .modal-footer {
  border-top: 1px solid #dee2e6;
  padding: 1rem 1.5rem;
}
</style>
